import React, { useEffect, useState, useCallback } from "react";
import { CalendarPlus, Loader2, Trash2, EyeOff, Eye, Moon, Sun, Sparkles, Save } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

const inputCls =
  "w-full rounded-xl border border-tara-gold/25 bg-white/85 px-4 py-2.5 text-sm text-tara-ink placeholder:text-tara-muted/70 outline-none focus:border-tara-gold/60";

const EMPTY = { name: "", name_bn: "", name_hi: "", date: "", note: "", kind: "special", service_id: "maa-tara-puja" };
const ICON = { purnima: Sun, kaushiki: Sparkles, mauni: Moon, diwali: Sparkles, amavasya: Moon, somvati: Moon, special: Sparkles };

export default function FestivalsPanel() {
  const [computed, setComputed] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [noteDrafts, setNoteDrafts] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/festivals");
      setComputed(r.data.computed || []);
      setOverrides(r.data.overrides || []);
    } catch { toast.error("Could not load calendar"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const overrideFor = (id) => overrides.find((o) => o.id === id);

  const patchComputed = async (id, patch) => {
    try {
      await api.patch(`/admin/festivals/${id}`, patch);
      toast.success("Calendar updated");
      load();
    } catch { toast.error("Update failed"); }
  };

  const addCustom = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.date) {
      toast.error("Name and date are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/festivals", form);
      toast.success("Special day added");
      setForm(EMPTY);
      load();
    } catch { toast.error("Could not add") }
    finally { setSaving(false); }
  };

  const removeCustom = async (id) => {
    try {
      await api.delete(`/admin/festivals/${id}`);
      toast.success("Removed");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const custom = overrides.filter((o) => !o.computed);

  return (
    <div className="grid lg:grid-cols-[380px_1fr] gap-8" data-testid="festivals-panel">
      <div className="space-y-6 h-fit lg:sticky lg:top-6">
        <form onSubmit={addCustom} className="glass-strong rounded-2xl p-6 space-y-4" data-testid="festival-form">
          <h3 className="font-serif text-lg font-bold text-tara-ink flex items-center gap-2">
            <CalendarPlus className="h-4 w-4 text-tara-gold" /> Add a special day
          </h3>
          <p className="text-xs text-tara-muted leading-relaxed">
            Amavasya and Purnima dates are computed automatically from the lunar calendar. Use this to add
            temple-specific festivals, anniversaries or special sadhana nights.
          </p>
          <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Name (English)" className={inputCls} data-testid="festival-name-input" />
          <input value={form.name_bn} onChange={(e) => setForm((f) => ({ ...f, name_bn: e.target.value }))} placeholder="নাম (বাংলা)" className={inputCls} data-testid="festival-name-bn-input" />
          <input value={form.name_hi} onChange={(e) => setForm((f) => ({ ...f, name_hi: e.target.value }))} placeholder="नाम (हिन्दी)" className={inputCls} data-testid="festival-name-hi-input" />
          <input type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} className={inputCls} data-testid="festival-date-input" />
          <textarea value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} rows={3} placeholder="What is observed on this day?" className={inputCls} data-testid="festival-note-input" />
          <button type="submit" disabled={saving} className="btn-sacred flex items-center gap-2 rounded-full px-6 py-3 text-sm font-serif font-semibold" data-testid="festival-submit-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarPlus className="h-4 w-4" />} Add to calendar
          </button>
        </form>

        {custom.length > 0 && (
          <div className="glass rounded-2xl p-5" data-testid="festival-custom-list">
            <h4 className="font-serif text-sm uppercase tracking-[0.18em] text-tara-goldDeep mb-4">Your special days</h4>
            <ul className="space-y-3">
              {custom.map((c) => (
                <li key={c.id} className="flex items-start justify-between gap-3" data-testid={`festival-custom-${c.id}`}>
                  <div>
                    <p className="font-display text-lg text-tara-ink leading-tight">{c.name}</p>
                    <p className="text-xs text-tara-muted">{c.date}</p>
                  </div>
                  <button onClick={() => removeCustom(c.id)} className="rounded-full border border-red-500/40 p-1.5 text-red-600 hover:bg-red-500/10 transition" data-testid={`festival-delete-${c.id}`} aria-label="Delete">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div>
        <h3 className="font-serif text-sm uppercase tracking-[0.18em] text-tara-goldDeep mb-4">Computed lunar calendar</h3>
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 text-tara-gold animate-spin" /></div>
        ) : (
          <div className="space-y-3" data-testid="festival-computed-list">
            {computed.map((f) => {
              const ov = overrideFor(f.id);
              const Ico = ICON[f.kind] || Moon;
              const hidden = ov?.hidden;
              const draft = noteDrafts[f.id];
              return (
                <div key={f.id} className={`glass rounded-2xl p-4 ${hidden ? "opacity-50" : ""}`} data-testid={`admin-festival-${f.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${
                        f.major ? "bg-gradient-to-br from-tara-vermilion to-tara-gold text-white" : "bg-white/80 border border-tara-gold/30 text-tara-gold"
                      }`}>
                        <Ico className="h-4 w-4" />
                      </span>
                      <div>
                        <p className="font-display text-lg text-tara-ink leading-tight">{ov?.name || f.name}</p>
                        <p className="text-xs text-tara-muted">{f.date} · {f.time_ist}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => patchComputed(f.id, { hidden: !hidden })}
                        className="rounded-full border border-tara-gold/30 p-2 text-tara-goldDeep hover:bg-tara-gold/10 transition"
                        data-testid={`festival-hide-${f.id}`}
                        aria-label="Toggle visibility"
                      >
                        {hidden ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="mt-3 flex flex-col sm:flex-row gap-2">
                    <input
                      defaultValue={ov?.name || ""}
                      onChange={(e) => setNoteDrafts((d) => ({ ...d, [f.id]: { ...d[f.id], name: e.target.value } }))}
                      placeholder={`Rename "${f.name}" (optional)`}
                      className={inputCls}
                      data-testid={`festival-rename-${f.id}`}
                    />
                    <input
                      defaultValue={ov?.note || ""}
                      onChange={(e) => setNoteDrafts((d) => ({ ...d, [f.id]: { ...d[f.id], note: e.target.value } }))}
                      placeholder="Custom note for devotees (optional)"
                      className={inputCls}
                      data-testid={`festival-note-${f.id}`}
                    />
                    {draft && (
                      <button
                        onClick={() => patchComputed(f.id, draft)}
                        className="btn-sacred flex items-center gap-2 rounded-full px-5 py-2.5 text-xs font-semibold whitespace-nowrap"
                        data-testid={`festival-save-${f.id}`}
                      >
                        <Save className="h-3.5 w-3.5" /> Save
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
